/**
 * Servicio de geocodificación inversa
 * Convierte coordenadas (latitud, longitud) en direcciones legibles.
 */

// URL base del servicio de geocodificación
const GEOCODE_API_BASE_URL = 'https://websocket.quro.com.mx:30508/api/v1/geocode';

const DEFAULT_PRECISION = 6;
const DEFAULT_TIMEOUT_MS = 4000;
const DEFAULT_CONCURRENCY = 6;

const REVERSE_GEOCODING_ENABLED =
  String(process.env.REACT_APP_ENABLE_REVERSE_GEOCODING ?? '').toLowerCase() === 'true';

export function isReverseGeocodingEnabled() {
  return REVERSE_GEOCODING_ENABLED;
}

const addressCache = new Map(); // key -> formattedAddress
const inFlight = new Map(); // key -> Promise<{formattedAddress?: string}>

let activeCount = 0;
const waitQueue = [];

function makeKey(lat, lng, precision = DEFAULT_PRECISION) {
  const nlat = Number(lat);
  const nlng = Number(lng);
  if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null;
  return `${nlat.toFixed(precision)},${nlng.toFixed(precision)}`;
}

function isAddressMissing(address) {
  if (!address) return true;
  const text = String(address).trim().toLowerCase();
  if (!text) return true;

  const missingValues = [
    'dirección no disponible',
    'direccion no disponible',
    'no disponible',
    'indeterminado',
    'n/a',
    'na'
  ];

  return missingValues.some(v => text === v || text.includes(v));
}

async function acquire(concurrency = DEFAULT_CONCURRENCY) {
  if (activeCount < concurrency) {
    activeCount += 1;
    return;
  }
  await new Promise(resolve => waitQueue.push(resolve));
  activeCount += 1;
}

function release() {
  activeCount = Math.max(0, activeCount - 1);
  const next = waitQueue.shift();
  if (next) next();
}

function withTimeout(promise, timeoutMs = DEFAULT_TIMEOUT_MS) {
  if (!timeoutMs || timeoutMs <= 0) return promise;

  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('timeout')), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

/**
 * Obtiene una dirección a partir de coordenadas (geocodificación inversa)
 * @param {number} lat
 * @param {number} lng
 * @param {(data: any) => void} callback
 * @param {(err: string) => void} [errorCallback]
 */
export function getReverseGeocode(lat, lng, callback, errorCallback) {
  if (!REVERSE_GEOCODING_ENABLED) {
    if (errorCallback) errorCallback('Geocodificación deshabilitada');
    return;
  }
  const nlat = Number(lat);
  const nlng = Number(lng);
  if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) {
    console.error('[geocode-service] Coordenadas inválidas', { lat, lng });
    if (errorCallback) errorCallback('Coordenadas inválidas');
    return;
  }

  const url = `${GEOCODE_API_BASE_URL}/reverse?lat=${encodeURIComponent(nlat)}&lng=${encodeURIComponent(nlng)}`;
  fetch(url)
    .then(r => r.json())
    .then(resp => {
      if (resp && (resp.success === true || resp.ok === true) && resp.data) {
        callback(resp.data);
      } else {
        console.warn('[geocode-service] Sin datos de geocodificación', resp);
        if (errorCallback) errorCallback('No se pudo obtener la dirección');
      }
    })
    .catch(err => {
      console.error('[geocode-service] Error en petición', err);
      if (errorCallback) errorCallback('Error de geocodificación');
    });
}

/**
 * Versión Promise con cache/deduplicación.
 * Devuelve el objeto data original y cachea formattedAddress.
 */
export async function getReverseGeocodeAsync(lat, lng, options = {}) {
  const { precision = DEFAULT_PRECISION, timeoutMs = DEFAULT_TIMEOUT_MS, concurrency = DEFAULT_CONCURRENCY } = options;

  const key = makeKey(lat, lng, precision);
  if (!key) {
    throw new Error('Coordenadas inválidas');
  }

  const cached = addressCache.get(key);
  if (cached) {
    return { formattedAddress: cached, fromCache: true };
  }

  if (!REVERSE_GEOCODING_ENABLED) {
    return { formattedAddress: '' };
  }

  const existing = inFlight.get(key);
  if (existing) {
    return withTimeout(existing, timeoutMs);
  }

  const task = (async () => {
    await acquire(concurrency);
    try {
      const data = await new Promise((resolve, reject) => {
        getReverseGeocode(lat, lng, resolve, (err) => reject(new Error(err || 'Error de geocodificación')));
      });

      const formattedAddress = data?.formattedAddress || data?.address || '';
      if (formattedAddress) {
        addressCache.set(key, formattedAddress);
      }

      return { ...data, formattedAddress };
    } finally {
      release();
    }
  })();

  inFlight.set(key, task);

  try {
    return await withTimeout(task, timeoutMs);
  } finally {
    inFlight.delete(key);
  }
}

export function getCachedAddress(lat, lng, options = {}) {
  const key = makeKey(lat, lng, options.precision ?? DEFAULT_PRECISION);
  if (!key) return null;
  return addressCache.get(key) || null;
}

/**
 * Actualiza un elemento por ID con la dirección obtenida (uso legacy opcional)
 */
export function updateAddressFromCoordinates(lat, lng, elementId) {
  const el = typeof document !== 'undefined' ? document.getElementById(elementId) : null;
  if (!el) return;
  el.innerHTML = 'Obteniendo dirección...';
  getReverseGeocode(
    lat,
    lng,
    data => {
      el.innerHTML = data?.formattedAddress || 'Dirección no disponible';
    },
    () => {
      el.innerHTML = 'No se pudo obtener la dirección';
    }
  );
}

/**
 * Wrapper para marcadores de Google (por compatibilidad)
 */
export function getAddressForMarker(marker, callback) {
  if (!marker || typeof marker.getPosition !== 'function') {
    callback({ formattedAddress: 'Dirección no disponible' });
    return;
  }
  const pos = marker.getPosition();
  getReverseGeocode(pos.lat(), pos.lng(), callback, () => callback({ formattedAddress: 'Dirección no disponible' }));
}

/**
 * Prefetch de direcciones faltantes de una estructura maestro/detalle (estadías).
 * No rechaza si alguna falla.
 */
export async function prefetchMissingAddressesFromEstadias(safeTableData, options = {}) {
  if (!REVERSE_GEOCODING_ENABLED) return { requested: 0, disabled: true };
  const tasks = [];

  (Array.isArray(safeTableData) ? safeTableData : []).forEach(group => {
    (Array.isArray(group?.children) ? group.children : []).forEach(child => {
      const addr = child?.direccion || child?.Direccion || '';
      if (!isAddressMissing(addr)) return;

      const lat = child?.latitud;
      const lng = child?.longitud;
      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;

      tasks.push(
        getReverseGeocodeAsync(lat, lng, options).catch(() => null)
      );
    });
  });

  if (tasks.length === 0) return { requested: 0 };

  // Esperar a que terminen (o timeout individual por tarea)
  await Promise.all(tasks);
  return { requested: tasks.length };
}

/**
 * Espera a que no haya peticiones en vuelo (con timeout).
 */
export async function awaitIdle(timeoutMs = 2500) {
  const start = Date.now();
  while (inFlight.size > 0) {
    if (Date.now() - start > timeoutMs) return { ok: false, pending: inFlight.size };
    await new Promise(r => setTimeout(r, 50));
  }
  return { ok: true, pending: 0 };
}

export const geocodeService = {
  getReverseGeocode,
  getReverseGeocodeAsync,
  getCachedAddress,
  updateAddressFromCoordinates,
  getAddressForMarker,
  prefetchMissingAddressesFromEstadias,
  awaitIdle,
  isAddressMissing,
  isReverseGeocodingEnabled
};
